/**
 * ShotModel — what the client and the server must compute identically about a
 * shot: where its pellets go, how big its hit circle is, and when the trigger
 * may fire again. Shared so the predicted tracer and the server's projectile
 * are the same projectile (lag compensation hardening, spec D2/D3).
 */
import { TICK_MS } from './Movement.js';

/** The player's hit circle before the projectile's own size is added. */
export const PLAYER_HIT_RADIUS_BASE = 40;

export function projectileHitRadius(projectileSize, fallbackSize = 4) {
  return PLAYER_HIT_RADIUS_BASE + (projectileSize || fallbackSize);
}

/** mulberry32: small, fast, identical in every JS engine. */
export function seededRandom(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Spread offsets (radians) for one trigger pull — one per pellet; a
 * single-projectile weapon gets one. `seed` is the shot's input seq when it
 * has one (client and server then roll the same pellets); null means
 * Math.random (bots, untagged shots). The formulas are the server's
 * historical ones: 'cone' spans ±pelletSpread (the client used to draw half
 * that — the tracers now match the damage).
 */
export function spreadOffsets(weapon, seed = null) {
  const rand = typeof seed === 'number' && Number.isFinite(seed)
    ? seededRandom(Math.imul(seed | 0, 0x9E3779B1))
    : Math.random;
  const deg = Math.PI / 180;
  const count = weapon.pelletCount || 1;
  const spread = weapon.spread || 0;
  const out = [];

  if (count > 1) {
    const pelletSpread = weapon.pelletSpread || 0;
    for (let i = 0; i < count; i++) {
      let a;
      if (weapon.spreadPattern === 'uniform') {
        a = (-pelletSpread / 2 + (pelletSpread / (count - 1)) * i) * deg;
      } else if (weapon.spreadPattern === 'cone') {
        // Preserve Weapon's historical triangular cone while making both
        // rolls deterministic when the shot carries an input-seq seed.
        const centered = (rand() + rand()) / 2;
        a = (centered - 0.5) * 2 * pelletSpread * deg;
      } else {
        a = (rand() - 0.5) * pelletSpread * deg;
      }
      if (spread > 0) a += (rand() - 0.5) * spread * deg;
      out.push(a);
    }
    return out;
  }

  out.push(spread > 0 ? (rand() - 0.5) * spread * deg : 0);
  return out;
}

/** Inputs that must separate two shots `intervalMs` apart (the input clock is one tick per input). */
export function ticksBetween(intervalMs) {
  return Math.max(1, Math.ceil(intervalMs / TICK_MS - 1e-6));
}

/**
 * May a shot tagged with input `seq` fire, given the last one fired at `lastSeq`?
 * Judged on input seqs, which the client and server both know exactly, so
 * network jitter cannot make a correctly spaced pair look too close.
 * A seq at or before the last shot is refused (no replaying old seqs).
 */
export function canFireAtSeq(lastSeq, seq, intervalMs) {
  if (typeof lastSeq !== 'number') return true;
  return seq - lastSeq >= ticksBetween(intervalMs);
}

/** The seq a trigger pull is tagged with: the last input the client has simulated. */
export function shotSeqFor(inputSeq) {
  return inputSeq > 0 ? inputSeq - 1 : null;
}
